import React, { useState, useEffect, useRef } from "react";
import { Button } from "../navbar/Button";
import { motion } from "framer-motion";
import { splashPending } from "../../splash";
import "./Hero.css";
import "../../App.css";

// Split once at module level so every letter keeps a stable global index.
let letterCount = 0;
const TITLE_WORDS = "CRISTAL INOX".split(" ").map((word) =>
  [...word].map((char) => ({ char, index: letterCount++ }))
);

// Wait for the intro curtain to open before revealing the hero.
const SPLASH_DELAY = 2.4;

function Hero() {
  const [videoSource, setVideoSource] = useState(
    window.innerWidth > 900 ? "/vedios/vedio2.mp4" : "/vedios/vedio4.mp4"
  );
  const [isLooping, setIsLooping] = useState(window.innerWidth > 900);
  const [baseDelay] = useState(() => (splashPending() ? SPLASH_DELAY : 0));
  const heroRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 900) {
        setVideoSource("/vedios/vedio2.mp4");
        setIsLooping(true);
      } else {
        setVideoSource("/vedios/vedio4.mp4");
        setIsLooping(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {}, [videoSource]);

  const handleVideoEnd = () => {
    if (isLooping) {
      setVideoSource((prev) =>
        prev === "/vedios/vedio2.mp4"
          ? "/vedios/vedio1.mp4"
          : "/vedios/vedio2.mp4"
      );
    }
  };

  // Moves the light spot with the cursor without re-rendering React.
  const handleMouseMove = (e) => {
    const el = heroRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - rect.left}px`);
    el.style.setProperty("--my", `${e.clientY - rect.top}px`);
  };

  const scrollDown = () => {
    window.scrollTo({ top: window.innerHeight, behavior: "smooth" });
  };

  return (
    <div
      className="hero-container"
      ref={heroRef}
      onMouseMove={handleMouseMove}
    >
      <video
        key={videoSource}
        src={videoSource}
        poster={
          videoSource === "/vedios/vedio4.mp4"
            ? "/vedios/poster-mobile.jpg"
            : "/vedios/poster-desktop.jpg"
        }
        autoPlay
        muted
        playsInline
        preload="auto"
        onEnded={handleVideoEnd}
      />
      <div className="hero-glow" />
      <h1 className="hero-title" aria-label="CRISTAL INOX">
        {TITLE_WORDS.map((letters, w) => (
          <span className="hero-word" key={w} aria-hidden="true">
            {letters.map(({ char, index }) => (
              <motion.span
                className="hero-letter"
                key={index}
                initial={{ opacity: 0, y: 60, filter: "blur(12px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                transition={{
                  duration: 0.8,
                  ease: [0.2, 0.65, 0.3, 0.9],
                  delay: baseDelay + 0.3 + index * 0.07,
                }}
                style={{ animationDelay: `${baseDelay + 1.8 + index * 0.1}s` }}
              >
                {char}
              </motion.span>
            ))}
          </span>
        ))}
      </h1>
      <motion.p
        className="hero-tagline"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: baseDelay + 1.1 }}
      >
        Chaudronnerie inox sur mesure
      </motion.p>
      <motion.div
        className="hero-btns"
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 1, ease: "easeOut", delay: baseDelay + 1.3 }}
      >
        <Button
          link="contact"
          className="btns"
          buttonStyle="btn--outline"
          buttonSize="btn--large"
        >
          Demander un Devis
        </Button>
        <Button
          link="catalogue"
          className="btns"
          buttonStyle="btn--primary"
          buttonSize="btn--large"
        >
          Explorer nos projets
        </Button>
      </motion.div>
      <motion.button
        type="button"
        className="hero-scroll"
        aria-label="Défiler vers le bas"
        onClick={scrollDown}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: baseDelay + 2 }}
      >
        <span className="hero-scroll__wheel" />
      </motion.button>
    </div>
  );
}

export default Hero;
